import React, { useEffect, useMemo, useState } from 'react';
import Modal from '../../../components/ui/Modal';
import Btn from '../../../components/ui/Btn';
import Field, { TextInput, NativeSelect } from '../../../components/ui/Field';
import WhereItLives, { validUrl } from './WhereItLives';

// Crear un agente. El proyecto puede ser uno que ya existe o uno nuevo, creado aquí mismo con lo mínimo
// (nombre y tipo de agente) y con la sección de Chats encendida; después se completa en Proyectos.
// `employeeProject`: el id del proyecto interno de un empleado. Un empleado no elige proyecto ni registra agentes externos: todo va a su proyecto.
export default function NewAgentModal({ open, onClose, projects, types, models, onCreate, publicCredentials = [], employeeProject = null }) {
  const [mode, setMode] = useState('new');           // new | existing
  const [projectId, setProjectId] = useState('');
  const [projectName, setProjectName] = useState('');
  const [projectDesc, setProjectDesc] = useState('');
  const [typeId, setTypeId] = useState('');
  const [name, setName] = useState('');
  const [nameTouchedByUser, setNameTouchedByUser] = useState(false);
  const [model, setModel] = useState('');
  const [credentialId, setCredentialId] = useState('');
  const [where, setWhere] = useState('platform');
  const [url, setUrl] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (!open) return;
    setMode(employeeProject || projects.length ? 'existing' : 'new'); setProjectId(employeeProject ? String(employeeProject) : ''); setProjectName(''); setProjectDesc('');
    const leads = types.find((t) => /leads/i.test(t.name || ''));
    setTypeId(String((leads || types[0])?.id ?? ''));
    setName(''); setNameTouchedByUser(false); setError(''); setTouched(false); setWhere('platform'); setUrl(''); setNote(''); setCredentialId('');
    setModel(models.includes('gpt-4.1-mini') ? 'gpt-4.1-mini' : models[0] || '');
    // Solo al abrir: si la lista de proyectos cambia con el diálogo abierto (p. ej. tras un error), no se borra lo escrito.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Si nadie escribió el nombre del agente, se toma el del proyecto nuevo.
  useEffect(() => { if (mode === 'new' && !nameTouchedByUser) setName(projectName); }, [projectName, mode, nameTouchedByUser]);

  const sorted = useMemo(() => [...projects].sort((a, b) => (a.name || '').localeCompare(b.name || '', 'es')), [projects]);
  const nameError = touched && name.trim().length < 2 ? 'Escribe un nombre de al menos 2 letras.' : '';
  const projectError = touched && mode === 'existing' && !projectId ? 'Elige el proyecto del cliente.' : '';
  const projectNameError = touched && mode === 'new' && (projectName.trim().length < 3 || projectName.trim().length > 100) ? 'De 3 a 100 caracteres.' : '';
  const typeError = touched && mode === 'new' && !typeId ? 'Elige el tipo de agente.' : '';
  const urlError = !validUrl(url) ? 'Debe empezar con http:// o https://.' : '';

  const submit = async (e) => {
    e?.preventDefault();
    setTouched(true);
    const projectOk = mode === 'existing' ? Boolean(projectId) : projectName.trim().length >= 3 && projectName.trim().length <= 100 && Boolean(typeId);
    if (!projectOk || name.trim().length < 2 || (where === 'platform' && !model) || urlError) return;
    setBusy(true); setError('');
    try {
      await onCreate({
        project: mode === 'existing' ? { id: Number(projectId) } : { name: projectName, description: projectDesc, type: types.find((t) => String(t.id) === typeId) },
        name, model, managedBy: where, externalUrl: url, externalNote: note,
        credentialId: where === 'platform' && credentialId ? Number(credentialId) : null,
      });
    } catch (err) { setError(err.message); setBusy(false); }
  };

  const submitLabel = where !== 'platform' ? 'Registrar agente externo' : mode === 'new' ? 'Crear proyecto y agente' : 'Crear agente';

  return (
    <Modal
      open={open}
      onClose={busy ? undefined : onClose}
      title="Nuevo agente"
      description="Si funciona aquí, después le das su clave, sus instrucciones y sus canales."
      footer={<>
        <Btn variant="ghost" onClick={onClose} disabled={busy}>Cancelar</Btn>
        <Btn variant="primary" onClick={submit} loading={busy}>{submitLabel}</Btn>
      </>}
    >
      <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
        {!employeeProject && (
        <fieldset className="space-y-3">
          <legend className="text-[13px] font-medium text-fg mb-1.5">Proyecto del cliente</legend>
          <div role="radiogroup" aria-label="Proyecto" className="flex rounded-lg border border-line p-0.5 bg-ink w-full sm:w-auto">
            {[['new', 'Proyecto nuevo'], ['existing', 'Uno que ya existe']].map(([k, l]) => (
              <button key={k} type="button" role="radio" aria-checked={mode === k} onClick={() => setMode(k)}
                className={`flex-1 sm:flex-none h-9 px-3 rounded-md text-[13px] font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-live/60
                  ${mode === k ? 'bg-raised text-fg' : 'text-fg-dim hover:text-fg'}`}>{l}</button>
            ))}
          </div>
          {mode === 'existing' ? (
            <Field label="Proyecto" error={projectError}>
              <NativeSelect value={projectId} onChange={(e) => setProjectId(e.target.value)} data-autofocus>
                <option value="">Elige un proyecto…</option>
                {sorted.map((p) => <option key={p.id} value={p.id}>{p.name || `Proyecto ${p.id}`}</option>)}
              </NativeSelect>
            </Field>
          ) : (
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Nombre del proyecto" error={projectNameError}>
                <TextInput value={projectName} onChange={(e) => setProjectName(e.target.value)} maxLength={100} autoComplete="off" placeholder="Clínica Dental Sonrisa" data-autofocus />
              </Field>
              <Field label="Tipo de agente" error={typeError}>
                <NativeSelect value={typeId} onChange={(e) => setTypeId(e.target.value)}>
                  <option value="">Elige un tipo…</option>
                  {types.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </NativeSelect>
              </Field>
              <Field label="Descripción" optional className="sm:col-span-2">
                <TextInput value={projectDesc} onChange={(e) => setProjectDesc(e.target.value)} maxLength={150} autoComplete="off" placeholder="Qué hace el cliente, en una línea" />
              </Field>
              <p className="sm:col-span-2 text-[12.5px] leading-relaxed text-fg-faint">
                Se crea con los módulos de su tipo y la sección de Chats encendida. Después lo encuentras en Proyectos para darle permisos, asignarle clientes o agregarle lo que falte.
              </p>
            </div>
          )}
        </fieldset>
        )}

        <Field label="Nombre del agente" error={nameError} hint="Como lo reconocerá tu equipo, por ejemplo «Asistente de ventas».">
          <TextInput value={name} onChange={(e) => { setName(e.target.value); setNameTouchedByUser(true); }} maxLength={120} autoComplete="off" />
        </Field>

        {!employeeProject && <WhereItLives value={where} onChange={setWhere} url={url} onUrl={setUrl} note={note} onNote={setNote} urlError={urlError} />}
        {where === 'platform' && (
          <Field label="Modelo" hint="Solo aparecen los modelos con precio en «Precios de modelos».">
            <NativeSelect value={model} onChange={(e) => setModel(e.target.value)}>
              {models.map((m) => <option key={m} value={m}>{m}</option>)}
            </NativeSelect>
          </Field>
        )}
        {where === 'platform' && publicCredentials.length > 0 && (
          <Field label="Clave de OpenAI" optional hint="Para una prueba de concepto elige una pública; después la puedes cambiar por otra o por una propia.">
            <NativeSelect value={credentialId} onChange={(e) => setCredentialId(e.target.value)}>
              <option value="">La asigno después</option>
              {publicCredentials.map((c) => <option key={c.id} value={c.id}>{c.label} · termina en {c.last4}</option>)}
            </NativeSelect>
          </Field>
        )}
        {error && <p role="alert" className="text-sm text-signal">{error}</p>}
        <button type="submit" hidden aria-hidden tabIndex={-1} />
      </form>
    </Modal>
  );
}
